import { useCallback, useState } from 'react';
import {
  Translate,
  useAuthProvider,
  useGetIdentity,
  useLogout,
  useStore,
} from 'ra-core';
import { ChevronsUpDown, LogOut } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { useAdvertiserData } from '@/layout/advertiserLayout/useAdvertiserData';
import { useLocation, useNavigate } from 'react-router';
import { replaceAdvertiserIdInPath } from '@/utils';

export function UserMenu() {
  const authProvider = useAuthProvider();
  const { data: user } = useGetIdentity();
  const logout = useLogout();
  const { advertisers } = useAdvertiserData();
  const [advertiserId] = useStore<string | undefined>('advertiser_id');
  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);

  const currentAdvertiser = advertisers.find(a => a.id === advertiserId);

  const handleAdvertiserChange = useCallback(
    (newAdvertiserId: string) => {
      if (newAdvertiserId && newAdvertiserId !== advertiserId) {
        navigate(replaceAdvertiserIdInPath(location.pathname, newAdvertiserId));
        setOpen(false);
      }
    },
    [advertiserId, location.pathname, navigate]
  );

  if (!authProvider) return null;
  if (!user) return null;

  const avatarFallback = user.fullName?.charAt(0);
  const firstName = user.fullName?.trim().split(' ')[0];

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            // `group/menu` so the chevron can target the trigger's
            // `data-state=open` without clashing with the unnamed `group` on
            // the sidebar wrapper (which carries `data-collapsible`).
            'group/menu flex w-full items-center gap-2.5 rounded-xl p-2 text-left outline-none',
            // Resting: a defined, gently raised account chip — not a bare row.
            'border border-border bg-secondary/40 shadow-premium',
            'transition-[background-color,border-color,box-shadow,transform] duration-200 ease-[var(--ease-premium)]',
            // Hover / open: brighten the edge, lift a hair, deepen the shadow.
            'hover:-translate-y-px hover:border-border-hover hover:bg-secondary hover:shadow-elev-2',
            'data-[state=open]:border-border-hover data-[state=open]:bg-secondary data-[state=open]:shadow-elev-2',
            'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
            // Collapsed icon rail: drop the chip — just the centered avatar.
            'group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:border-0 group-data-[collapsible=icon]:bg-transparent group-data-[collapsible=icon]:p-0 group-data-[collapsible=icon]:shadow-none group-data-[collapsible=icon]:hover:translate-y-0 group-data-[collapsible=icon]:hover:bg-transparent group-data-[collapsible=icon]:hover:shadow-none'
          )}
        >
          <div className="relative shrink-0">
            <Avatar className="size-10 rounded-lg border border-border">
              <AvatarImage src={user.avatar} alt={user.fullName} />
              <AvatarFallback className="rounded-lg">
                {avatarFallback}
              </AvatarFallback>
            </Avatar>
            {/* Active-account node — emerald (the app's "Active" accent),
                ringed so it reads as a chip on the avatar's corner. */}
            <span
              aria-hidden
              className="absolute -bottom-0.5 -right-0.5 size-3 rounded-full bg-emerald ring-2 ring-secondary group-data-[collapsible=icon]:ring-sidebar"
            />
          </div>
          <div className="flex min-w-0 flex-1 flex-col group-data-[collapsible=icon]:hidden">
            <span className="truncate text-sm font-semibold leading-5 tracking-tight text-sidebar-foreground">
              {firstName}
            </span>
            <span className="truncate text-xs font-medium leading-4 tracking-tight text-muted-foreground">
              {currentAdvertiser?.name}
            </span>
          </div>
          {/* Chevron flips 180° when the menu is open — subtle motion
              that signals the popper's state without redrawing the icon. */}
          <ChevronsUpDown
            className={cn(
              'ml-auto size-4 shrink-0 transition-transform duration-200 ease-[var(--ease-premium)]',
              'group-data-[state=open]/menu:rotate-180',
              'group-data-[collapsible=icon]:hidden'
            )}
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="top"
        align="start"
        // Figma spec: 8px gap between the dropdown and the line (i.e. the
        // SidebarFooter's top border), matching the 8px horizontal
        // breathing room on either side. Composition:
        //   8px footer top padding + 1px border (the line) + 8px gap
        //   = 17px total from the trigger's top edge to the dropdown.
        sideOffset={17}
        // Width-locked to the trigger so the menu is a vertical extension
        // of the user-card.
        style={{ width: 'var(--radix-dropdown-menu-trigger-width)' }}
        className={cn(
          // Base surface — mirrors the trigger chip's eliteness: rounded-xl,
          // a lit top edge, real elevation, and a translucent fill so the
          // backdrop blur actually frosts what's behind it.
          'flex flex-col gap-1 rounded-xl border border-border border-t-white/10 bg-secondary/90 p-1.5 shadow-elev-3 backdrop-blur-xl',
          // Premium open/close motion:
          // • zoom-in-90 (vs the default 95) gives the menu a more
          //   pronounced "grow out of the trigger" feel — paired with
          //   Radix's transform-origin (already set to the bottom for
          //   `side=top`), the menu visually expands upward from the
          //   user-card.
          // • EASE_PREMIUM (the same cubic-bezier used across the app)
          //   replaces the generic ease and adds a confident settle.
          // • Slightly longer in (220ms), snappy out (140ms).
          'data-[state=open]:zoom-in-90 data-[state=closed]:zoom-out-95',
          'data-[state=open]:duration-200 data-[state=closed]:duration-150',
          'ease-[var(--ease-premium)]'
        )}
      >
        {advertisers.length > 1 &&
          advertisers.map(advertiser => {
            const isActive = advertiser.id === advertiserId;
            return (
              <DropdownMenuItem
                key={advertiser.id}
                onClick={() => handleAdvertiserChange(advertiser.id)}
                className={cn(
                  'cursor-pointer gap-2.5 rounded-lg p-1.5 focus:bg-muted',
                  isActive && 'bg-accent focus:bg-accent'
                )}
              >
                <div className="relative shrink-0">
                  <Avatar className="size-7 rounded-md border border-border">
                    <AvatarFallback className="rounded-md text-[11px] font-medium">
                      {advertiser.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  {isActive && (
                    <span
                      aria-hidden
                      className="absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full bg-emerald ring-2 ring-accent"
                    />
                  )}
                </div>
                <span className="min-w-0 flex-1 truncate text-sm font-medium">
                  {advertiser.name}
                </span>
              </DropdownMenuItem>
            );
          })}
        {advertisers.length > 1 && <DropdownMenuSeparator />}
        <DropdownMenuItem
          variant="destructive"
          onClick={() => logout()}
          className="cursor-pointer gap-2.5 rounded-lg p-1.5"
        >
          <span className="flex size-7 shrink-0 items-center justify-center">
            <LogOut className="!text-destructive size-5" />
          </span>
          <Translate i18nKey="ra.auth.logout">Log out</Translate>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
